import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const userData = localStorage.getItem('admin_user');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        if (hasAdminPrivileges(parsedUser)) {
          setUser(parsedUser);
          authService.getCurrentUser()
            .then(currentUser => {
              if (hasAdminPrivileges(currentUser)) {
                setUser(currentUser);
                localStorage.setItem('admin_user', JSON.stringify(currentUser));
              } else {
                console.warn('User no longer has admin privileges');
                logout();
              }
            })
            .catch((error) => {
              console.error('Token validation failed:', error);
              logout();
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          logout();
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        logout();
      }
    } else {
      setLoading(false);
    }
  }, []);

  const hasAdminPrivileges = (user) => {
    // Admin panel access requires user_type 'admin' AND is_staff true
    return user && user.user_type === 'admin' && user.is_staff;
  };

  const isSuperAdmin = (user) => {
    return user && user.user_type === 'admin' && user.is_staff && user.is_superuser;
  };

  const isLegalAdmin = (user) => {
    return user && user.user_type === 'admin' && user.is_staff && user.legal_admin === true;
  };

  const isBusinessAdmin = (user) => {
    return user && user.user_type === 'admin' && user.is_staff && user.business_admin === true;
  };

  const isStaff = (user) => {
    return user && user.user_type === 'admin' && user.is_staff && 
           !user.is_superuser && !user.legal_admin && !user.business_admin;
  };

  const getUserRole = (user) => {
    if (!user) return 'Unknown';
    if (isSuperAdmin(user)) return 'Super Admin';
    if (isLegalAdmin(user)) return 'Legal Admin';
    if (isBusinessAdmin(user)) return 'Business Admin';
    if (isStaff(user)) return 'Staff';
    return 'User';
  };

  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials);
      
      if (hasAdminPrivileges(response)) {
        setUser(response);
        localStorage.setItem('admin_token', response.token);
        localStorage.setItem('admin_user', JSON.stringify(response));
        return response;
      } else {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        throw new Error('Access denied. Admin privileges required.');
      }
    } catch (error) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    authService.logout().catch(console.error);
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isSuperAdmin: user ? isSuperAdmin(user) : false,
    isLegalAdmin: user ? isLegalAdmin(user) : false,
    isBusinessAdmin: user ? isBusinessAdmin(user) : false,
    isStaff: user ? isStaff(user) : false,
    getUserRole: user ? getUserRole(user) : 'Unknown',
    hasAdminPrivileges: user ? hasAdminPrivileges(user) : false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}